import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDialog } from '../../context/DialogContext'
import { AppLayout } from '../../components/layouts/AppLayout'
import { Button } from '../../components/ui/button'
import { Card, CardContent } from '../../components/ui/card'
import { Buildings, Plus, MagnifyingGlass } from '@phosphor-icons/react'
import { getAllCompanies } from '../../services/companies'

export default function CompaniesPage() {
  const navigate = useNavigate()
  const { showError } = useDialog()
  const [companies, setCompanies] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadCompanies() {
      try {
        const data = await getAllCompanies()
        setCompanies(data)
      } catch (error) {
        console.error('Error loading companies:', error)
        showError('Erro', 'Erro ao carregar empresas')
      } finally {
        setLoading(false)
      }
    }
    loadCompanies()
  }, [])

  return (
    <AppLayout>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Buildings size={24} className="text-emerald-600" weight="duotone" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Empresas</h1>
                <p className="text-sm text-gray-500">Gerencie embarcadores e agentes de carga</p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/master/companies/new')}
              className="bg-emerald-600 hover:bg-emerald-700 gap-2"
            >
              <Plus size={20} weight="bold" />
              Nova Empresa
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500">Carregando...</div>
          </div>
        ) : companies.length === 0 ? (
          <Card>
            <CardContent className="pt-16 pb-16">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                  <Buildings size={32} className="text-gray-400" weight="duotone" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Nenhuma empresa cadastrada
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  Comece criando sua primeira empresa (embarcador ou agente de carga)
                </p>
                <Button
                  onClick={() => navigate('/master/companies/new')}
                  className="bg-emerald-600 hover:bg-emerald-700 gap-2"
                >
                  <Plus size={20} weight="bold" />
                  Criar Primeira Empresa
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {companies.map((company) => (
              <Card key={company.companyId} className="hover:shadow-md transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <Buildings size={24} className="text-emerald-600" weight="duotone" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {company.tradingName || company.name}
                          </h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            company.companyType === 'shipper'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-purple-100 text-purple-700'
                          }`}>
                            {company.companyType === 'shipper' ? 'Embarcador' : 'Agente de Carga'}
                          </span>
                        </div>
                        {company.tradingName && (
                          <p className="text-sm text-gray-600 mb-2">
                            Razão Social: {company.name}
                          </p>
                        )}
                        <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                          {company.primaryIdentifier && (
                            <div>
                              <span className="font-medium">{company.primaryIdentifierType}:</span> {company.primaryIdentifier}
                            </div>
                          )}
                          {company.email && (
                            <div>
                              <span className="font-medium">Email:</span> {company.email}
                            </div>
                          )}
                          {company.phone && (
                            <div>
                              <span className="font-medium">Telefone:</span> {company.phone}
                            </div>
                          )}
                          <div>
                            <span className="font-medium">País:</span> {company.countryCode}
                          </div>
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/master/companies/${company.companyId}?type=${company.companyType}`)}
                    >
                      Ver Detalhes
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </AppLayout>
  )
}
